import { useEffect, useMemo } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { makeStyles, withStyles } from "@mui/styles";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import { useContext } from "react";
import { GlobalContext } from "../App";
import { Divider, Paper } from "@mui/material";
import { MapFilter } from "./MapFilter";
import { getDistance, orderByDistance } from "geolib";

const autocompleteService = {
  current: null,
};

const StyledTextField = withStyles({
  root: {
    "& label.Mui-focused": {
      color: "white",
    },
    "& .MuiInputBase-root": {
      color: "white",
    },
    "& .MuiFormLabel-root": {
      color: "white",
    },
    "& .MuiAutocomplete-popupIndicator": {
      color: "rgb(255 255 255 / 54%)",
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
      padding: "2px 8px",
    },
    "& .MuiInputLabel-outlined": {
      transform: "translate(10px, 10px) scale(1)",
      transition: "0.2s all",
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, 0px) scale(0.75)",
      opacity: "0",
    },
    "& .MuiIconButton-label": {
      color: "white",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSvgIcon-root": {
      fill: "white",
    },
  },
})(TextField);

const useStyles = makeStyles((theme) => ({
  icon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(2),
  },
  paper: {
    backgroundColor: "#294a59 !important",
    display: "flex",
    borderRadius: "100vw !important",
    border: "1px solid white",
    padding: "0px 10px",
  },
  divider: {
    margin: "10px 0px",
    backgroundColor: "rgb(255 255 255 / 56%)",
  },
}));

export default function GoogleMaps() {
  const classes = useStyles();

  const {
    setCenter,
    zoom,
    setZoom,
    value,
    setValue,
    inputValue,
    setInputValue,
    options,
    setOptions,
    allClinics,
    setVisibleClinicCardIDs,
    setSearchValueCoordinates,
  } = useContext(GlobalContext);

  /**
   * Finds clinics within a 5km radius or the closest 5 clinics if less than 2 are within 5km.
   * @param {Object} referenceLocation - The reference location with latitude and longitude.
   * @param {Array} clinics - Array of clinic objects with latitude and longitude.
   * @returns {Array} - Array of clinics that meet the criteria.
   */
  const findClinics = (referenceLocation, clinics) => {
    // Order clinics by distance from the reference location
    const orderedClinics = orderByDistance(
      referenceLocation,
      clinics.map((clinic) => ({
        ...clinic.acfData.address,
        id: clinic.id, // Assuming each clinic has a unique id
        distance: getDistance(referenceLocation, clinic.acfData.address),
      }))
    );

    // Filter clinics within a 5km radius
    const clinicsWithinRadius = orderedClinics.filter(
      (clinic) => clinic.distance <= 10000
    );

    // If there are less than 2 clinics within 5km, get the closest 5 clinics
    if (clinicsWithinRadius.length < 2) {
      return orderedClinics
        .slice(0, 2)
        .map((clinic) => clinics.find((c) => c.id === clinic.id));
    }

    return clinicsWithinRadius.map((clinic) =>
      clinics.find((c) => c.id === clinic.id)
    );
  };

  useEffect(() => {
    if (window.google && value !== null) {
      // console.log({ place_id: value.place_id });
      new window.google.maps.Geocoder().geocode(
        {
          placeId: value.place_id,
        },
        (results, status) => {
          if (status === "OK") {
            if (allClinics) {
              setVisibleClinicCardIDs(
                findClinics(
                  {
                    latitude: results[0].geometry.location.lat(),
                    longitude: results[0].geometry.location.lng(),
                  },
                  allClinics
                ).map((n) => n.id)
              );
            }

            setCenter({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });

            if (zoom < 11) {
              setZoom(11);
            }

            setSearchValueCoordinates({
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(
          {
            ...request,
            types: ["(regions)"],
            componentRestrictions: { country: "uk" },
          },
          callback
        );
      }, 1000),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (inputValue.length >= 3) {
      fetch({ input: inputValue }, (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          if (["bsf.e0t.com", "localhost"].includes(window.location.hostname)) {
            console.info("Request: Places API Fired ", { results });
          }

          setOptions(newOptions);
        }
      });
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      freeSolo={true}
      noOptionsText="No results"
      includeInputInList
      filterSelectedOptions
      value={value}
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <Paper className={classes.paper} square={false}>
          <StyledTextField
            {...params}
            label="Search location"
            variant="outlined"
            fullWidth
          />
          {window.shouldShowFilter && (
            <>
              <Divider
                className={classes.divider}
                orientation="vertical"
                flexItem
              />
              <MapFilter />
            </>
          )}
        </Paper>
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOnIcon sx={{ color: "text.secondary" }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
